import { useCallback, useState } from 'react';

interface UseUnsavedChangesGuardParams {
  /** Callback that actually closes the container (sheet, modal, etc.). */
  onClose: () => void;
}

interface UseUnsavedChangesGuardResult {
  /** Closes the confirmation dialog and keeps the container open ("keep editing"). */
  cancelClose: () => void;
  /** Confirms the close: dismisses the dialog, resets the dirty flag and closes the container. */
  confirmClose: () => void;
  /** Whether the confirmation dialog is currently open. */
  isConfirmOpen: boolean;
  /** Whether the guarded form currently has unsaved changes. */
  isDirty: boolean;
  /** Requests a close: closes directly when pristine, opens the confirmation dialog when dirty. */
  requestClose: () => void;
  /** Dirty-state setter — wire it to the form's `onDirtyChange` prop. */
  setIsDirty: (isDirty: boolean) => void;
}

/**
 * Guards the close action of a container (sheet/modal) that hosts a form with
 * potentially unsaved changes.
 *
 * Every close path (cancel button, X, overlay click, ESC) should be routed
 * through `requestClose`: it closes directly when the form is pristine and
 * opens a confirmation dialog when it is dirty. `confirmClose` discards the
 * changes and closes; `cancelClose` returns to the container with the form
 * values intact.
 *
 * In addition to the core API (`isDirty`, `requestClose`, `confirmClose`,
 * `cancelClose`, `isConfirmOpen`), the hook exposes `setIsDirty` so the host
 * can forward the form's `onDirtyChange` notifications — the guard owns the
 * dirty flag because the form and the close controls live in different
 * subtrees.
 *
 * @param params - `onClose` callback that performs the actual close
 * @returns Guard state and handlers
 *
 * @example
 * const { cancelClose, confirmClose, isConfirmOpen, requestClose, setIsDirty } = useUnsavedChangesGuard({ onClose });
 */
export const useUnsavedChangesGuard = ({ onClose }: UseUnsavedChangesGuardParams): UseUnsavedChangesGuardResult => {
  const [isDirty, setIsDirty] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const requestClose = useCallback(() => {
    if (!isDirty) {
      onClose();
      return;
    }
    setIsConfirmOpen(true);
  }, [isDirty, onClose]);

  const confirmClose = useCallback(() => {
    setIsConfirmOpen(false);
    setIsDirty(false);
    onClose();
  }, [onClose]);

  const cancelClose = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  return { cancelClose, confirmClose, isConfirmOpen, isDirty, requestClose, setIsDirty };
};
